import { useMonitoredProducts } from '@/api/queries'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { LoadingBlock } from '@/components/ui/Spinner'
import { useLocalStorage } from '@/lib/hooks'
import styles from './MonitoringPage.module.css'
import { QueuesCard } from './QueuesCard'
import { ServicesCard } from './ServicesCard'
import type { Target } from './target'

const queuesId = ({ product, environment }: Target) => `queues:${product}:${environment}`
const servicesId = ({ product, environment }: Target) => `services:${product}:${environment}`

export function MonitoringPage() {
  const { data: products, isLoading, error } = useMonitoredProducts()
  const [stored, setStored] = useLocalStorage<string[]>('monitoring.openCards', [])
  // The stored value is user-editable, so do not trust its shape.
  const open = Array.isArray(stored) ? stored : []
  const toggle = (id: string) => setStored(open.includes(id) ? open.filter((item) => item !== id) : [...open, id])

  if (error) return <ErrorState error={error} />
  if (isLoading || !products) return <LoadingBlock />

  const queueTargets = products.flatMap((p) => p.environments.map((environment) => ({ product: p.name, environment })))
  // Instances are only watched in production, as in the previous cockpit.
  const serviceTargets = products.flatMap((p) => {
    const environment = p.environments.find((e) => e.toLowerCase() === 'production')
    return environment ? [{ product: p.name, environment }] : []
  })

  return (
    <div className={styles.page}>
      <section className={styles.section} aria-labelledby="monitoring-queues">
        <h2 id="monitoring-queues" className={styles.sectionTitle}>
          Queues
        </h2>
        {queueTargets.length === 0 ? (
          <EmptyState>No product to monitor</EmptyState>
        ) : (
          <div className={styles.grid}>
            {queueTargets.map((target) => (
              <QueuesCard key={queuesId(target)} target={target} open={open.includes(queuesId(target))} onToggle={() => toggle(queuesId(target))} />
            ))}
          </div>
        )}
      </section>

      <section className={styles.section} aria-labelledby="monitoring-instances">
        <h2 id="monitoring-instances" className={styles.sectionTitle}>
          Instances
        </h2>
        {serviceTargets.length === 0 ? (
          <EmptyState>No product has a production environment</EmptyState>
        ) : (
          <div className={styles.grid}>
            {serviceTargets.map((target) => (
              <ServicesCard key={servicesId(target)} target={target} open={open.includes(servicesId(target))} onToggle={() => toggle(servicesId(target))} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
